import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { BRAND_OPTIONS } from "../shared/brands";
import { LOCATION_REGIONS, getLocationTownships } from "./formData";
import { buildParticipantsWorkbook, type ExcelPhoto } from "./adminExcel";
import dkshLogo from "./assets/dksh-logo.png";

type Identity = { actor: string; email: string | null; mode: "local" | "cloudflare-access" };
type Dashboard = {
  campaign: { code: string; title: string; startsAt: string; closesAt: string; phase: string };
  totals: { total: number; today: number; lastSevenDays: number; photoMetadata: number };
  brandCounts: Record<string, number>;
  refreshedAt: string;
};
type ListItem = {
  entryNo: string; submittedAt: string; fullName: string; maskedNrc: string; maskedPhone: string;
  city: string; township: string; cityCode: string | null; townshipCode: string | null;
  brands: string[]; photoAvailable: boolean; entryMode: "TEST" | "LIVE";
};
type Detail = {
  entryNo: string; submittedAt: string; fullName: string; nrc: string; phone: string;
  city: string; township: string; walletType: string | null; walletAccountNo: string | null;
  brands: string[]; displayStand: { code: string; label: string | null } | null;
  consentVersion: string; consentAcceptedAt: string;
  photo: { contentType: string; sizeBytes: number; available: boolean };
  audit: { submittedAt: string }; status: string; entryMode: "TEST" | "LIVE";
};
type Filters = {
  search: string; from: string; to: string; cityCode: string; townshipCode: string;
  brandCode: string; photoAvailable: string; entryMode: string;
};

const EMPTY_FILTERS: Filters = { search: "", from: "", to: "", cityCode: "", townshipCode: "", brandCode: "", photoAvailable: "", entryMode: "" };
const TOKEN_KEY = "dksh-local-admin-token";

function formatDateTime(value: string) {
  const date = new Date(value);
  return {
    date: new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Yangon", day: "2-digit", month: "short", year: "numeric" }).format(date),
    time: new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Yangon", hour: "2-digit", minute: "2-digit", hour12: false }).format(date)
  };
}

function formatBytes(bytes: number) {
  return bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

async function adminFetch(path: string, token: string, options?: RequestInit): Promise<Response> {
  const headers = new Headers(options?.headers);
  headers.set("accept", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  return fetch(path, { ...options, headers, cache: "no-store" });
}

async function responseError(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: { message?: string } };
    return body.error?.message ?? `Request failed (${response.status}).`;
  } catch {
    return `Request failed (${response.status}).`;
  }
}

async function fetchAdminPhoto(entryNo: string, token: string, signal?: AbortSignal): Promise<Blob | null> {
  const response = await adminFetch(`/api/admin/entries/${encodeURIComponent(entryNo)}/photo`, token, { signal });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await responseError(response));
  return response.blob();
}

async function photoForExcel(blob: Blob): Promise<ExcelPhoto> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = objectUrl;
    await image.decode();
    const canvas = document.createElement("canvas");
    canvas.width = 800; canvas.height = 800;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Unable to prepare the photo for Excel.");
    context.fillStyle = "#ffffff"; context.fillRect(0, 0, 800, 800);
    const scale = Math.min(800 / image.naturalWidth, 800 / image.naturalHeight);
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    context.drawImage(image, Math.round((800 - width) / 2), Math.round((800 - height) / 2), width, height);
    return { base64: canvas.toDataURL("image/jpeg", .9), extension: "jpeg" };
  } finally { URL.revokeObjectURL(objectUrl); }
}

function ParticipantPhoto({ entryNo, token, available }: { entryNo: string; token: string; available: boolean }) {
  const [visible, setVisible] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [missing, setMissing] = useState(!available);
  const hostRef = useCallback((node: HTMLDivElement | null) => {
    if (!node || !available) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: "300px" });
    observer.observe(node);
  }, [available]);

  useEffect(() => {
    if (!visible || !available) return;
    const controller = new AbortController();
    let objectUrl = "";
    fetchAdminPhoto(entryNo, token, controller.signal).then((blob) => {
      if (!blob) { setMissing(true); return; }
      objectUrl = URL.createObjectURL(blob); setPhotoUrl(objectUrl);
    }).catch(() => { if (!controller.signal.aborted) setMissing(true); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [available, entryNo, token, visible]);

  return <div className="admin-list-photo" ref={hostRef}>
    {photoUrl && <img src={photoUrl} alt={`Display submission ${entryNo}`} />}
    {!photoUrl && <span>{missing ? "Photo unavailable" : "Loading photo…"}</span>}
  </div>;
}

function SignIn({ onAuthenticated }: { onAuthenticated: (identity: Identity, token: string) => void }) {
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      const response = await adminFetch("/api/admin/session", token);
      if (!response.ok) throw new Error(await responseError(response));
      const body = await response.json() as { identity: Identity };
      sessionStorage.setItem(TOKEN_KEY, token);
      onAuthenticated(body.identity, token);
      setToken("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to sign in.");
    } finally { setBusy(false); }
  }

  return <main className="admin-auth-page">
    <section className="admin-auth-card" aria-labelledby="admin-sign-in-title">
      <div className="admin-logo">DKSH</div>
      <p className="admin-kicker">Read-only workspace</p>
      <h1 id="admin-sign-in-title">Admin access</h1>
      <p>Enter the local development token from the ignored <code>.dev.vars</code> file.</p>
      <div className="admin-dev-badge">Local development only</div>
      <form onSubmit={submit}>
        <label>Local Admin token<input type="password" autoComplete="off" value={token} onChange={(event) => setToken(event.target.value)} required /></label>
        {error && <p className="admin-error" role="alert">{error}</p>}
        <button type="submit" disabled={busy}>{busy ? "Checking…" : "Sign in"}</button>
      </form>
      <a href="/?stand=DKSH_Myanmar_GT">Return to participant form</a>
    </section>
  </main>;
}

function AccessRequired({ onRetry }: { onRetry: () => void }) {
  return <main className="admin-auth-page">
    <section className="admin-auth-card" aria-labelledby="admin-access-title">
      <div className="admin-logo">DKSH</div>
      <p className="admin-kicker">Read-only workspace</p>
      <h1 id="admin-access-title">Cloudflare Access required</h1>
      <p>Sign in through the approved DKSH Access policy, then retry this page.</p>
      <button type="button" onClick={onRetry}>Retry access check</button>
      <a href="/?stand=DKSH_Myanmar_GT">Return to participant form</a>
    </section>
  </main>;
}

function DetailDialog({ entryNo, token, onClose }: { entryNo: string; token: string; onClose: () => void }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoState, setPhotoState] = useState<"loading" | "ready" | "missing">("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = "";
    (async () => {
      try {
        const response = await adminFetch(`/api/admin/entries/${encodeURIComponent(entryNo)}`, token, { signal: controller.signal });
        if (!response.ok) throw new Error(await responseError(response));
        const body = await response.json() as { entry: Detail };
        setDetail(body.entry);
        if (!body.entry.photo.available) { setPhotoState("missing"); return; }
        const photo = await fetchAdminPhoto(entryNo, token, controller.signal);
        if (!photo) { setPhotoState("missing"); return; }
        objectUrl = URL.createObjectURL(photo);
        setPhotoUrl(objectUrl); setPhotoState("ready");
      } catch (caught) {
        if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : "Unable to load entry.");
      }
    })();
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [entryNo, token]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", close);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", close); };
  }, [onClose]);

  const submitted = detail ? formatDateTime(detail.submittedAt) : null;
  return <div className="admin-modal-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
    <section className="admin-detail" role="dialog" aria-modal="true" aria-labelledby="entry-detail-title">
      <header><div><p>Participant detail</p><h2 id="entry-detail-title">{entryNo}</h2></div><button type="button" onClick={onClose} aria-label="Close participant detail">×</button></header>
      <div className="admin-detail-body">
        {error && <p className="admin-error" role="alert">{error}</p>}
        {!detail && !error && <p className="admin-loading">Loading participant…</p>}
        {detail && <>
          <div className="admin-detail-grid">
            <Info label="Submitted" value={`${submitted?.date} · ${submitted?.time} MMT`} />
            <Info label="Status" value={detail.status} />
            <Info label="Entry mode" value={detail.entryMode} />
            <Info label="Full name" value={detail.fullName} />
            <Info label="NRC" value={detail.nrc} sensitive />
            <Info label="Phone" value={detail.phone} sensitive />
            <Info label="City / State / Region" value={detail.city} />
            <Info label="Township" value={detail.township} />
            <Info label="Wallet type" value={detail.walletType ?? "—"} />
            <Info label="Wallet account" value={detail.walletAccountNo ?? "—"} sensitive />
            <Info label="Selected brands" value={detail.brands.join(", ") || "—"} wide />
            <Info label="Display stand" value={detail.displayStand ? `${detail.displayStand.code} · ${detail.displayStand.label ?? ""}` : "—"} wide />
            <Info label="Consent" value={`${detail.consentVersion} · ${formatDateTime(detail.consentAcceptedAt).date}`} wide />
            <Info label="Photo metadata" value={`${detail.photo.contentType} · ${formatBytes(detail.photo.sizeBytes)}`} wide />
            <Info label="Submission audit timestamp" value={detail.audit.submittedAt} wide />
          </div>
          <section className="admin-photo-panel" aria-label="Private participant photo">
            <h3>Private photo</h3>
            {photoState === "loading" && <p>Loading authorized preview…</p>}
            {photoState === "missing" && <p className="admin-empty">Photo is missing from private storage.</p>}
            {photoState === "ready" && <img src={photoUrl} alt="Authorized participant display submission" />}
          </section>
        </>}
      </div>
    </section>
  </div>;
}

function Info({ label, value, sensitive, wide }: { label: string; value: string; sensitive?: boolean; wide?: boolean }) {
  return <div className={`admin-info${wide ? " wide" : ""}`}><span>{label}</span><strong className={sensitive ? "sensitive" : ""}>{value}</strong></div>;
}

function Workspace({ identity, token, onSignOut }: { identity: Identity; token: string; onSignOut: () => void }) {
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [searchDraft, setSearchDraft] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([]);
  const [items, setItems] = useState<ListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedEntry, setSelectedEntry] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exporting, setExporting] = useState<"csv" | "xlsx" | null>(null);
  const [exportProgress, setExportProgress] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => ({ ...current, search: searchDraft.trim() }));
      setCursor(null); setCursorHistory([]);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  const params = useMemo(() => {
    const result = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value) result.set(key, value); });
    result.set("pageSize", String(pageSize));
    if (cursor) result.set("cursor", cursor);
    return result;
  }, [filters, pageSize, cursor]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError("");
    Promise.all([
      adminFetch("/api/admin/dashboard", token, { signal: controller.signal }).then(async (response) => {
        if (!response.ok) throw new Error(await responseError(response));
        return response.json() as Promise<Dashboard>;
      }),
      adminFetch(`/api/admin/entries?${params}`, token, { signal: controller.signal }).then(async (response) => {
        if (!response.ok) throw new Error(await responseError(response));
        return response.json() as Promise<{ items: ListItem[]; nextCursor: string | null }>;
      })
    ]).then(([dashboardResult, entriesResult]) => {
      setDashboard(dashboardResult); setItems(entriesResult.items); setNextCursor(entriesResult.nextCursor);
    }).catch((caught) => { if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : "Unable to load Admin data."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [params, refreshKey, token]);

  function changeFilter(name: keyof Filters, value: string) {
    setFilters((current) => {
      const next = { ...current, [name]: value };
      if (name === "cityCode") next.townshipCode = "";
      return next;
    });
    setCursor(null); setCursorHistory([]);
  }

  const filterTownships = getLocationTownships(filters.cityCode);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  async function exportCsv() {
    setExporting("csv"); setError("");
    try {
      const exportParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => { if (value) exportParams.set(key, value); });
      const response = await adminFetch(`/api/admin/export.csv?${exportParams}`, token);
      if (!response.ok) throw new Error(await responseError(response));
      const objectUrl = URL.createObjectURL(await response.blob());
      const disposition = response.headers.get("content-disposition") ?? "";
      const filename = disposition.match(/filename="([^"]+)"/)?.[1] ?? "dksh-export.csv";
      const link = document.createElement("a"); link.href = objectUrl; link.download = filename; link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Export failed."); }
    finally { setExporting(null); }
  }

  async function exportExcelWithPhotos() {
    setExporting("xlsx"); setExportProgress("Preparing data…"); setError("");
    try {
      const exportParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => { if (value) exportParams.set(key, value); });
      const response = await adminFetch(`/api/admin/export.csv?${exportParams}`, token);
      if (!response.ok) throw new Error(await responseError(response));
      const disposition = response.headers.get("content-disposition") ?? "";
      const csvFilename = disposition.match(/filename="([^"]+)"/)?.[1] ?? "dksh-export.csv";
      const workbook = await buildParticipantsWorkbook(await response.text(), async (entryNo) => {
        const photo = await fetchAdminPhoto(entryNo, token);
        return photo ? photoForExcel(photo) : null;
      }, (completed, total) => setExportProgress(`Preparing photos ${completed}/${total}…`));
      const objectUrl = URL.createObjectURL(new Blob([workbook], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = objectUrl; link.download = csvFilename.replace(/\.csv$/i, "-with-photos.xlsx"); link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Excel export failed."); }
    finally { setExporting(null); setExportProgress(""); }
  }

  return <main className="admin-shell">
    <header className="admin-topbar">
      <div><img className="admin-topbar-logo" src={dkshLogo} alt="DKSH" /><span>Scan &amp; Win · Read-only Admin</span></div>
      <div className="admin-identity"><span>{identity.email ?? identity.actor}</span>{identity.mode === "local" && <em>LOCAL DEV</em>}<button type="button" onClick={onSignOut}>Sign out</button></div>
    </header>
    <div className="admin-content">
      <section className="admin-heading">
        <div><p>{dashboard?.campaign.code ?? "Campaign"}</p><h1>{dashboard?.campaign.title ?? "Admin workspace"}</h1><span>{dashboard ? `${formatDateTime(dashboard.campaign.startsAt).date} – ${formatDateTime(dashboard.campaign.closesAt).date} · ${dashboard.campaign.phase}` : "Loading campaign…"}</span></div>
        <div className="admin-heading-actions"><button type="button" onClick={() => setRefreshKey((value) => value + 1)}>Refresh</button><button className="primary" type="button" disabled={Boolean(exporting)} onClick={exportCsv}>{exporting === "csv" ? "Exporting…" : "Export CSV"}</button><button className="primary" type="button" disabled={Boolean(exporting)} onClick={exportExcelWithPhotos}>{exporting === "xlsx" ? (exportProgress || "Preparing Excel…") : "Excel with Photos"}</button></div>
      </section>

      {dashboard?.campaign.phase === "TEST" && <p className="admin-banner"><strong>TEST MODE — Entries are not eligible for the prize draw</strong><br />စမ်းသပ်မုဒ် — ဤစာရင်းများသည် ကံစမ်းမဲဆုအတွက် အကျုံးမဝင်ပါ။</p>}

      <section className="admin-summary" aria-label="Campaign totals">
        {[["Total entries", dashboard?.totals.total], ["Today (MMT)", dashboard?.totals.today], ["Last 7 days", dashboard?.totals.lastSevenDays], ["Photo metadata", dashboard?.totals.photoMetadata]].map(([label, value]) => <article key={String(label)}><span>{label}</span><strong>{value ?? "—"}</strong></article>)}
      </section>
      <section className="admin-brand-summary">
        {BRAND_OPTIONS.map((brand) => <div key={brand.code}><span>{brand.label}</span><strong>{dashboard?.brandCounts[brand.code] ?? "—"}</strong></div>)}
      </section>

      <section className="admin-list-card">
        <div className="admin-list-toolbar">
          <label className="admin-search"><span className="sr-only">Search participants</span><input placeholder="Search entry, name, NRC or phone" value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} maxLength={100} /></label>
          <button className="filter-toggle" type="button" onClick={() => setFiltersOpen((value) => !value)}>Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}</button>
        </div>
        <div className={`admin-filters${filtersOpen ? " open" : ""}`}>
          <label>From<input type="date" value={filters.from} onChange={(event) => changeFilter("from", event.target.value)} /></label>
          <label>To<input type="date" value={filters.to} onChange={(event) => changeFilter("to", event.target.value)} /></label>
          <label>City / Region<select value={filters.cityCode} onChange={(event) => changeFilter("cityCode", event.target.value)}><option value="">All</option>{LOCATION_REGIONS.map((region) => <option key={region.code} value={region.code}>{region.nameEn}</option>)}</select></label>
          <label>Township<select value={filters.townshipCode} disabled={!filters.cityCode} onChange={(event) => changeFilter("townshipCode", event.target.value)}><option value="">All</option>{filterTownships.map((township) => <option key={township.code} value={township.code}>{township.nameEn}</option>)}</select></label>
          <label>Brand<select value={filters.brandCode} onChange={(event) => changeFilter("brandCode", event.target.value)}><option value="">All</option>{BRAND_OPTIONS.map((brand) => <option key={brand.code} value={brand.code}>{brand.label}</option>)}</select></label>
          <label>Photo<select value={filters.photoAvailable} onChange={(event) => changeFilter("photoAvailable", event.target.value)}><option value="">All</option><option value="true">Available metadata</option><option value="false">Missing metadata</option></select></label>
          <label>Entry mode<select value={filters.entryMode} onChange={(event) => changeFilter("entryMode", event.target.value)}><option value="">All</option><option value="TEST">TEST</option><option value="LIVE">LIVE</option></select></label>
          <button type="button" onClick={() => { setFilters(EMPTY_FILTERS); setSearchDraft(""); setCursor(null); setCursorHistory([]); }}>Reset filters</button>
        </div>

        {error && <p className="admin-error admin-banner" role="alert">{error}</p>}
        {loading && <p className="admin-loading">Loading entries…</p>}
        {!loading && !items.length && <p className="admin-empty">No participants match these filters.</p>}
        {!loading && items.length > 0 && <div className="admin-table-wrap"><table>
          <thead><tr><th>Entry</th><th>Mode</th><th>Submitted</th><th>Participant</th><th>NRC / Phone</th><th>Location</th><th>Brands</th><th>Photo</th><th><span className="sr-only">Action</span></th></tr></thead>
          <tbody>{items.map((item) => { const stamp = formatDateTime(item.submittedAt); return <tr key={item.entryNo}>
            <td data-label="Entry"><strong>{item.entryNo}</strong></td>
            <td data-label="Mode"><strong>{item.entryMode}</strong></td>
            <td data-label="Submitted">{stamp.date}<small>{stamp.time} MMT</small></td>
            <td data-label="Participant">{item.fullName}</td>
            <td data-label="NRC / Phone"><span>{item.maskedNrc}</span><small>{item.maskedPhone}</small></td>
            <td data-label="Location"><span>{item.city}</span><small>{item.township}</small></td>
            <td data-label="Brands"><div className="admin-tags">{item.brands.map((brand) => <span key={brand}>{brand}</span>)}</div></td>
            <td data-label="Photo"><ParticipantPhoto entryNo={item.entryNo} token={token} available={item.photoAvailable} /></td>
            <td><button className="view-button" type="button" onClick={() => setSelectedEntry(item.entryNo)}>View</button></td>
          </tr>; })}</tbody>
        </table></div>}

        <footer className="admin-pagination">
          <label>Rows<select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setCursor(null); setCursorHistory([]); }}><option>25</option><option>50</option><option>100</option></select></label>
          <span>Showing {items.length} entries</span>
          <div><button type="button" disabled={!cursorHistory.length} onClick={() => { const previous = [...cursorHistory]; setCursor(previous.pop() ?? null); setCursorHistory(previous); }}>Previous</button><button type="button" disabled={!nextCursor} onClick={() => { setCursorHistory((history) => [...history, cursor]); setCursor(nextCursor); }}>Next</button></div>
        </footer>
      </section>
      <p className="admin-refreshed">Last refreshed: {dashboard ? formatDateTime(dashboard.refreshedAt).time : "—"} MMT</p>
    </div>
    {selectedEntry && <DetailDialog entryNo={selectedEntry} token={token} onClose={() => setSelectedEntry(null)} />}
  </main>;
}

export default function AdminApp() {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? "");
  const [checking, setChecking] = useState(Boolean(token) || !import.meta.env.DEV);

  const authenticate = useCallback((nextIdentity: Identity, nextToken: string) => { setIdentity(nextIdentity); setToken(nextToken); setChecking(false); }, []);

  useEffect(() => {
    if (!checking) return;
    adminFetch("/api/admin/session", token).then(async (response) => {
      if (!response.ok) throw new Error("unauthorized");
      const body = await response.json() as { identity: Identity };
      setIdentity(body.identity);
    }).catch(() => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); }).finally(() => setChecking(false));
  }, [checking, token]);

  if (checking) return <main className="admin-auth-page"><p className="admin-loading">Checking Admin access…</p></main>;
  if (!identity) {
    if (!import.meta.env.DEV) return <AccessRequired onRetry={() => setChecking(true)} />;
    return <SignIn onAuthenticated={authenticate} />;
  }
  return <Workspace identity={identity} token={token} onSignOut={() => { sessionStorage.removeItem(TOKEN_KEY); setIdentity(null); setToken(""); }} />;
}
